import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Send, X } from 'lucide-react';
import { Section } from '@/shared/ui/layout/Section';
import { Textarea } from '@/shared/ui/inputs/Textarea';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { usePerm } from '@/shared/hooks/usePerm';
import { apiBroadcastPost } from '@/entities/broadcast';
import {
  broadcastSchema,
  type BroadcastFormValues,
} from '@/features/broadcast/modules/types';

export const FeaturesBroadcastBroadcastForm = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const canSend = usePerm('broadcast.send');
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const form = useForm<BroadcastFormValues>({
    resolver: zodResolver(broadcastSchema),
    defaultValues: { text_uz: '', text_ru: '', text_en: '', text_kk: '' },
  });

  const clearImage = (): void => {
    form.setValue('image', undefined, { shouldValidate: true });
    if (inputRef.current) inputRef.current.value = '';
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const send = useMutate({
    api: (vals: BroadcastFormValues) =>
      apiBroadcastPost({
        text_uz: vals.text_uz,
        text_ru: vals.text_ru,
        text_en: vals.text_en,
        text_kk: vals.text_kk,
        image: vals.image,
      }),
    onSuccess: (res) => {
      toast.success(t('broadcast.toast.sent', { count: res.created }));
      form.reset({ text_uz: '', text_ru: '', text_en: '', text_kk: '' });
      clearImage();
    },
    onError: (_e, message) => toast.error(message),
  });

  const textUzError = form.formState.errors.text_uz?.message;
  const textRuError = form.formState.errors.text_ru?.message;
  const textEnError = form.formState.errors.text_en?.message;
  const textKkError = form.formState.errors.text_kk?.message;
  const imageError = form.formState.errors.image?.message;
  const imageFile = form.watch('image');

  return (
    <form onSubmit={form.handleSubmit((v) => send.mutate(v))} className="flex flex-col gap-4">
      <Section title={t('broadcast.form.textSection')} description={t('broadcast.form.textHint')}>
        <div className="flex flex-col gap-4">
          <Textarea
            rows={5}
            label={t('broadcast.form.fields.textUz')}
            required
            placeholder={t('broadcast.form.fields.textUzPlaceholder')}
            error={textUzError ? t(textUzError) : undefined}
            {...form.register('text_uz')}
          />
          <Textarea
            rows={5}
            label={t('broadcast.form.fields.textRu')}
            placeholder={t('broadcast.form.fields.textRuPlaceholder')}
            error={textRuError ? t(textRuError) : undefined}
            {...form.register('text_ru')}
          />
          <Textarea
            rows={5}
            label={t('broadcast.form.fields.textEn')}
            placeholder={t('broadcast.form.fields.textEnPlaceholder')}
            error={textEnError ? t(textEnError) : undefined}
            {...form.register('text_en')}
          />
          <Textarea
            rows={5}
            label={t('broadcast.form.fields.textKk')}
            placeholder={t('broadcast.form.fields.textKkPlaceholder')}
            hint={t('broadcast.form.fields.textKkHint')}
            error={textKkError ? t(textKkError) : undefined}
            {...form.register('text_kk')}
          />
        </div>
      </Section>

      <Section title={t('broadcast.form.imageSection')} description={t('broadcast.form.imageHint')}>
        <Controller
          control={form.control}
          name="image"
          render={({ field }) => (
            <div className="flex flex-col gap-3">
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const picked = e.target.files?.[0];
                  field.onChange(picked ?? undefined);
                  setPreview((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return picked ? URL.createObjectURL(picked) : null;
                  });
                }}
              />
              {imageFile && preview ? (
                <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <img
                    src={preview}
                    alt=""
                    className="h-20 w-20 shrink-0 rounded-lg object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{imageFile.name}</p>
                    <p className="text-xs text-ink-muted">
                      {(imageFile.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                  <IconButton label={t('common.delete')} variant="ghost" onClick={clearImage}>
                    <X className="h-4 w-4" />
                  </IconButton>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  leftIcon={<ImagePlus className="h-4 w-4" />}
                  onClick={() => inputRef.current?.click()}
                >
                  {t('broadcast.form.pickImage')}
                </Button>
              )}
              {imageError && (
                <p className="text-xs font-medium text-red-600">{t(imageError)}</p>
              )}
            </div>
          )}
        />
      </Section>

      {canSend && (
        <div className="flex justify-end">
          <Button type="submit" leftIcon={<Send className="h-4 w-4" />} loading={send.isPending}>
            {t('broadcast.form.submit')}
          </Button>
        </div>
      )}
    </form>
  );
};
